// Product Card Color Swatch Switcher
// Dynamically updates product card content when color swatch is clicked

class ProductCardSwatchSwitcher {
  constructor() {
    this.switching = false; // Prevent multiple simultaneous switches
    this.prefetchedImages = new Set(); // Track prefetched images
    this.init();
  }

  init() {
    // Use event delegation to handle clicks on color swatches
    // Use capture phase (true) to ensure we get the event before other handlers
    document.addEventListener('click', this.handleSwatchClick.bind(this), true);

    // Prefetch images when product cards are hovered or visible
    this.setupImagePrefetching();
  }

  setupImagePrefetching() {
    // Prefetch images on mouseenter over product cards
    document.addEventListener(
      'mouseenter',
      (event) => {
        const productCard = event.target.closest('product-card');
        if (productCard && !productCard.dataset.prefetchStarted) {
          this.prefetchProductImages(productCard);
          productCard.dataset.prefetchStarted = 'true';
        }
      },
      true
    );
  }

  async prefetchProductImages(productCard) {
    const swatchesContainer = productCard.querySelector(
      '.product-card__color-swatches-swiper'
    );
    if (!swatchesContainer) return;

    const swatches = swatchesContainer.querySelectorAll(
      '.product-card__color-swatch[data-product-handle]'
    );

    // Prefetch product data and images for all swatches
    swatches.forEach((swatch) => {
      const productHandle = swatch.dataset.productHandle;
      if (!productHandle) return;

      // Prefetch the product card HTML to extract image URLs
      this.prefetchProductCard(productHandle);
    });
  }

  async prefetchProductCard(productHandle) {
    try {
      const fetchUrl = `${
        window.Shopify?.routes?.root || '/'
      }products/${productHandle}?view=card`;

      const response = await fetch(fetchUrl);
      if (!response.ok) return;

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const productCard = doc.querySelector('product-card');
      if (!productCard) return;

      // Extract and prefetch all image URLs from the product card
      const images = productCard.querySelectorAll(
        'img[src], img[data-src], source[srcset]'
      );
      images.forEach((img) => {
        let imageUrl = img.src || img.dataset.src;

        // Handle srcset attribute
        if (!imageUrl && img.srcset) {
          const srcset = img.getAttribute('srcset') || img.srcset;
          imageUrl = srcset.split(',')[0]?.trim().split(' ')[0];
        }

        // Handle source elements with srcset
        if (!imageUrl && img.srcset && img.tagName === 'SOURCE') {
          const srcset = img.getAttribute('srcset');
          imageUrl = srcset?.split(',')[0]?.trim().split(' ')[0];
        }

        if (imageUrl && !this.prefetchedImages.has(imageUrl)) {
          // Use Image constructor for better browser support
          const prefetchImage = new Image();
          prefetchImage.src = imageUrl;
          this.prefetchedImages.add(imageUrl);
        }
      });
    } catch (error) {
      // Silently fail prefetching - it's just an optimization
      console.debug('Prefetch failed for product:', productHandle);
    }
  }

  async handleSwatchClick(event) {
    // Handle clicks on the swatch itself or its child elements (like the span label)
    const swatch = event.target.closest('.product-card__color-swatch');
    if (!swatch) return;

    // Prevent default link behavior and stop propagation early
    event.preventDefault();
    event.stopPropagation();

    const productCard = swatch.closest('product-card');
    if (!productCard) return;

    // Prevent multiple simultaneous switches
    if (this.switching) return;

    // Don't switch if already selected
    if (swatch.classList.contains('product-card__color-swatch--selected')) {
      return;
    }

    const productHandle = swatch.dataset.productHandle;
    const productUrl = swatch.dataset.productUrl;
    const productId = swatch.dataset.productId;

    if (!productHandle || !productUrl) {
      // Fallback to navigation if data is missing
      if (productUrl) {
        window.location.href = productUrl;
      }
      return;
    }

    this.switchProductCard(
      productCard,
      productHandle,
      productUrl,
      productId,
      swatch
    );
  }

  async switchProductCard(
    productCard,
    productHandle,
    productUrl,
    productId,
    clickedSwatch
  ) {
    this.switching = true;

    // Get product info element once for use throughout the function
    const productInfo = productCard.querySelector('.product-card__info');

    try {
      // Add loading state to product info area only
      if (productInfo) {
        productInfo.classList.add('product-card__info--loading');
      }

      // Fetch new product card HTML (no loading state on whole card)
      const fetchUrl = `${
        window.Shopify?.routes?.root || '/'
      }products/${productHandle}?view=card`;
      const response = await fetch(fetchUrl);

      if (!response.ok) {
        throw new Error(`Failed to fetch product: ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newProductCard = doc.querySelector('product-card');

      if (!newProductCard) {
        throw new Error('Product card not found in response');
      }

      // Extract key elements for cross-fade animation
      const oldMedia = productCard.querySelector('.product-card__media');
      const oldContent = productCard.querySelector('.product-card__content');
      const oldVariants = productCard.querySelector('.product-card__variants');
      const oldOptions = productCard.querySelector('.product-card__options');
      const oldQuickAdd = productCard.querySelector(
        '.product-card__quick-add-overlay'
      );
      const oldActions = productCard.querySelector('.product-card__actions');

      const newMedia = newProductCard.querySelector('.product-card__media');
      const newContent = newProductCard.querySelector('.product-card__content');
      const newVariants = newProductCard.querySelector(
        '.product-card__variants'
      );
      const newOptions = newProductCard.querySelector('.product-card__options');
      const newQuickAdd = newProductCard.querySelector(
        '.product-card__quick-add-overlay'
      );
      const newActions = newProductCard.querySelector('.product-card__actions');
      const newSwatchesContainer = newProductCard.querySelector(
        '.product-card__color-swatches-swiper'
      );

      // PRIORITY: Replace images immediately, then fade them in smoothly
      if (oldMedia && newMedia) {
        oldMedia.innerHTML = newMedia.innerHTML;
        const imgs = oldMedia.querySelectorAll('img');
        imgs.forEach((img) => {
          img.classList.add('media-fade-in');
          const activate = () => {
            requestAnimationFrame(() => {
              img.classList.add('media-fade-in--active');
            });
          };
          if (img.complete) {
            activate();
          } else {
            img.addEventListener('load', activate, { once: true });
            img.addEventListener('error', activate, { once: true });
          }
        });
      }

      // Replace actions content (buttons, quick-add overlay, etc.)
      if (oldActions && newActions) {
        oldActions.innerHTML = newActions.innerHTML;
      }

      // Update product card classes immediately
      productCard.className = newProductCard.className;

      // Extract and preserve old swatches container BEFORE replacing content
      const oldSwatchesContainer = oldContent?.querySelector(
        '.product-card__color-swatches-swiper'
      );
      let swatchesClone = null;

      // Clone the swatches container (we'll reinsert it after content replacement)
      if (oldSwatchesContainer) {
        swatchesClone = oldSwatchesContainer.cloneNode(true);
      }

      // Handle swatches: move selected swatch to beginning (using the clone we'll reinsert)
      if (swatchesClone) {
        const swiperWrapper = swatchesClone.querySelector('.swiper-wrapper');

        if (swiperWrapper) {
          // Find the swatch that matches the new product ID
          const allSwatches = swiperWrapper.querySelectorAll('.swiper-slide');
          let selectedSwatch = null;

          allSwatches.forEach((slide) => {
            const swatch = slide.querySelector('.product-card__color-swatch');
            if (swatch && swatch.dataset.productId === productId) {
              selectedSwatch = slide;
            }
          });

          // If we found the selected swatch, move it to the beginning
          if (
            selectedSwatch &&
            selectedSwatch !== swiperWrapper.firstElementChild
          ) {
            swiperWrapper.insertBefore(
              selectedSwatch,
              swiperWrapper.firstElementChild
            );
          }

          // Update selected class
          swiperWrapper
            .querySelectorAll('.product-card__color-swatch')
            .forEach((sw) => {
              sw.classList.remove('product-card__color-swatch--selected');
              if (sw.dataset.productId === productId) {
                sw.classList.add('product-card__color-swatch--selected');
              }
            });
        }
      }

      // Replace content elements (excluding swatches since we're preserving the old ones)
      if (oldContent && newContent) {
        // Store new content HTML but remove swatches from it
        const contentClone = newContent.cloneNode(true);
        const newSwatchesContainer = contentClone.querySelector(
          '.product-card__color-swatches-swiper'
        );

        // Remove swatches from the clone since we're keeping the old ones
        if (newSwatchesContainer) {
          newSwatchesContainer.remove();
        }

        // Replace content (without swatches)
        oldContent.innerHTML = contentClone.innerHTML;

        // Re-insert the preserved swatches container (with reordered swatch) at the correct position
        if (swatchesClone) {
          const infoDiv = oldContent.querySelector('.product-card__info');
          if (infoDiv) {
            // Insert after vendor if it exists, otherwise at the beginning
            const vendor = infoDiv.querySelector('.product-card__vendor');
            if (vendor && vendor.nextSibling) {
              infoDiv.insertBefore(swatchesClone, vendor.nextSibling);
            } else {
              infoDiv.insertBefore(swatchesClone, infoDiv.firstChild);
            }

            // Re-initialize Swiper on the reinserted container
            requestAnimationFrame(() => {
              if (typeof Swiper !== 'undefined') {
                const swiperOptions = JSON.parse(
                  swatchesClone.getAttribute('data-swiper-options') || '{}'
                );
                const newSwiper = new Swiper(swatchesClone, {
                  freeMode: true,
                  slidesPerView: 'auto',
                  spaceBetween: 8,
                  enabled: true,
                  ...swiperOptions,
                });
                // Scroll to beginning immediately (no animation to avoid blip)
                newSwiper.slideTo(0, 0);
              }
            });
          }
        }

        // Prepare for fade in animation
        oldContent.style.opacity = '0';
        oldContent.style.transform = 'translateX(-20px)';
      }
      if (oldOptions && newOptions) {
        oldOptions.innerHTML = newOptions.innerHTML;
      }
      if (oldVariants && newVariants) {
        oldVariants.innerHTML = newVariants.innerHTML;
      }

      // Fade in new content with left-to-right animation for product info
      await this.fadeInContent(oldContent);

      // Remove loading state from product info
      if (productInfo) {
        productInfo.classList.remove('product-card__info--loading');
      }

      // Re-initialize event listeners
      this.reinitializeEventListeners(productCard);
    } catch (error) {
      console.error('Error switching product card:', error);

      // Remove loading state on error
      if (productInfo) {
        productInfo.classList.remove('product-card__info--loading');
      }

      // Fallback to navigation on error
      window.location.href = productUrl;
    } finally {
      this.switching = false;
    }
  }

  fadeInContent(contentElement) {
    return new Promise((resolve) => {
      if (!contentElement) {
        resolve();
        return;
      }

      // Animate product content from left to right with fade
      contentElement.style.transition =
        'opacity 0.4s ease-out, transform 0.4s ease-out';

      // Use requestAnimationFrame to ensure the initial state is applied first
      requestAnimationFrame(() => {
        setTimeout(() => {
          contentElement.style.opacity = '1';
          contentElement.style.transform = 'translateX(0)';
        }, 10);
      });

      setTimeout(() => {
        contentElement.style.transition = '';
        contentElement.style.transform = '';
        resolve();
      }, 400); // Wait for content animation to complete
    });
  }

  reinitializeEventListeners(productCard) {
    // Re-initialize quick cart drawer triggers
    const quickCartDrawer = document.querySelector('quick-cart-drawer');
    if (quickCartDrawer && typeof quickCartDrawer.initTriggers === 'function') {
      quickCartDrawer.initTriggers();
    }

    // Re-initialize quick add functionality
    // The QuickAddToCart class uses event delegation, so it should work automatically
    // But we can trigger a custom event to ensure it's aware of new buttons
    const quickAddEvent = new CustomEvent('productCardUpdated', {
      detail: { productCard },
    });
    document.dispatchEvent(quickAddEvent);

    // Re-initialize Swiper if present
    const swiperContainer = productCard.querySelector(
      '.product-card__color-swatches-swiper'
    );
    if (swiperContainer && typeof Swiper !== 'undefined') {
      // Check if swiper instance exists
      const swiper = swiperContainer.swiper;
      if (swiper) {
        swiper.update();
      } else {
        // Initialize new swiper if needed
        const swiperOptions = swiperContainer.dataset.swiperOptions
          ? JSON.parse(swiperContainer.dataset.swiperOptions)
          : {};
        new Swiper(swiperContainer, swiperOptions);
      }
    }

    // Re-initialize product variant selectors if needed
    const variantSelects = productCard.querySelectorAll(
      'product-variant-selects'
    );
    variantSelects.forEach((select) => {
      if (select.connectedCallback) {
        // Force re-connection if custom element
        select.connectedCallback();
      }
    });
  }
}

// Initialize when DOM is ready
try {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new ProductCardSwatchSwitcher();
    });
  } else {
    new ProductCardSwatchSwitcher();
  }
} catch (error) {
  console.error('Error initializing ProductCardSwatchSwitcher:', error);
}
